/**
 * Mô hình thuần cho báo giá dạng bảng tính (phần D): đoán vai trò theo tên cột,
 * đổi lưới của máy chủ sang ô của FortuneSheet và ngược lại. Không gọi API.
 */
import { normalizeUomLabel } from '@/lib/uom'

export type SheetRole = 'identity' | 'lookup' | 'compute' | 'info' | 'input' | 'uom' | 'result'

export const SHEET_ROLE_LABEL: Record<SheetRole, string> = {
  identity: 'Tên hàng',
  lookup: 'Quy cách / màu',
  compute: 'Đơn giá (dùng để tính)',
  info: 'Chỉ mô tả',
  input: 'Nhập lúc mua',
  uom: 'Đơn vị (ĐVT)',
  result: 'Giá mua (kết quả)',
}

// 'input' vẫn đọc được ở bản nháp cũ nhưng không còn đưa vào lựa chọn: ô nhập lúc mua do máy tự suy
// (ô trống được công thức dùng).
export const SHEET_ROLE_OPTIONS: SheetRole[] = ['identity', 'lookup', 'compute', 'result', 'uom', 'info']

export type SheetGridCell = string | number | boolean | null
export type SheetGrid = SheetGridCell[][]

/** FortuneSheet có thể trả giá trị từ dropdown dưới dạng ô hoặc object mô tả. */
export function quoteSheetCellText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value && typeof value === 'object') {
    const cell = value as Record<string, unknown>
    for (const key of ['v', 'value', 'text', 'label', 'displayValue']) {
      if (cell[key] !== undefined && cell[key] !== value) {
        const text = quoteSheetCellText(cell[key])
        if (text) return text
      }
    }
  }
  return ''
}

export const QUOTE_SHEET_UOM_OPTIONS = ['Pcs', 'm', 'm²', 'm³', 'kg'] as const

const QUOTE_SHEET_UOM_ALIASES: Record<string, (typeof QUOTE_SHEET_UOM_OPTIONS)[number]> = {
  pcs: 'Pcs', cái: 'Pcs', cai: 'Pcs',
  m: 'm', met: 'm', 'mét': 'm', md: 'm',
  m2: 'm²', 'm²': 'm²',
  m3: 'm³', 'm³': 'm³', khối: 'm³', khoi: 'm³',
  kg: 'kg',
}

export function normalizeQuoteSheetUnit(value: string): { value: string; error: boolean } {
  const key = value.trim().toLocaleLowerCase('vi')
  const normalized = QUOTE_SHEET_UOM_ALIASES[key] ?? QUOTE_SHEET_UOM_ALIASES[normalizeUomLabel(value)]
  return normalized ? { value: normalized, error: false } : { value, error: Boolean(key) }
}

export function normalizeQuoteSheetUnits(grid: SheetGrid): { grid: SheetGrid; problems: string[] } {
  const out = grid.map((row) => [...row])
  const roles = readRoleRow(out[0] ?? []).roles
  const headers = out[1] ?? []
  const unitCols = headers.flatMap((header, col) => {
    const title = typeof header === 'string' ? header.trim().toLocaleLowerCase('vi') : ''
    return roles[col] === 'uom' || title === 'đv đơn giá' ? [col] : []
  })
  const problems: string[] = []
  for (let rowIndex = 2; rowIndex < out.length; rowIndex++) {
    for (const col of unitCols) {
      const cell = out[rowIndex]?.[col]
      if (typeof cell !== 'string' || !cell.trim()) continue
      const result = normalizeQuoteSheetUnit(cell)
      if (result.error) problems.push(`Dòng ${rowIndex + 1}: đơn vị '${cell.trim()}' không có trong danh sách`)
      else out[rowIndex][col] = result.value
    }
  }
  return { grid: out, problems }
}

export function reindexQuoteSheetRows(grid: SheetGrid): SheetGrid {
  const out = grid.map((row) => [...row])
  const headers = out[1] ?? []
  const roles = readRoleRow(out[0] ?? []).roles
  const sttCol = headers.findIndex((header) => typeof header === 'string' && header.trim().toLocaleLowerCase('vi') === 'stt')
  const nameCol = roles.findIndex((role) => role === 'identity')
  if (sttCol < 0 || nameCol < 0) return out
  let number = 0
  for (let rowIndex = 2; rowIndex < out.length; rowIndex++) {
    const name = out[rowIndex]?.[nameCol]
    if (typeof name === 'string' && name.trim()) out[rowIndex][sttCol] = ++number
    else out[rowIndex][sttCol] = null
  }
  return out
}

/** Cờ hiện trên PO giữ theo tên tiêu đề để không lệch khi chèn/xóa/đổi thứ tự cột. */
export type PoVisibleByHeader = Record<string, boolean>

export type PoEyeState = 'on' | 'off' | 'always' | 'none'

/** Trạng thái biểu tượng bật/tắt PO; không sửa nội dung tiêu đề. */
export function poEyeStateForColumn(
  header: SheetGridCell | undefined,
  role: SheetRole | null,
  visible: boolean,
  locked: boolean,
): PoEyeState {
  const title = typeof header === 'string' ? header.trim().toLocaleLowerCase('vi') : ''
  if (!title || title === 'stt' || (role !== null && ['identity', 'compute', 'uom', 'result'].includes(role))) return 'none'
  if (locked || role === 'input') return 'always'
  return visible ? 'on' : 'off'
}

export function togglePoVisibleColumn(current: boolean[], column: number): boolean[] {
  const next = [...current]
  if (column >= 0 && column < next.length) next[column] = !next[column]
  return next
}

export function isPoEyeHit(x: number, y: number, rect: { left: number; top: number; right: number; bottom: number }): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

export function poVisibleByHeader(grid: SheetGrid, visible: boolean[] | Record<string, boolean> | Record<number, boolean> | null | undefined): PoVisibleByHeader {
  const headers = grid[1] ?? []
  const roles = grid[0] ?? []
  const fixedRoles = new Set(['Tên hàng', 'Đơn giá (dùng để tính)', 'Giá mua (kết quả)'])
  const out: PoVisibleByHeader = {}
  headers.forEach((header, col) => {
    const name = typeof header === 'string' ? header.trim() : ''
    if (!name || fixedRoles.has(String(roles[col] ?? ''))) return
    if (Array.isArray(visible)) {
      out[name] = visible[col] === true
    } else if (visible && typeof visible === 'object') {
      out[name] = (visible as Record<string, boolean>)[name] === true || (visible as Record<number, boolean>)[col] === true
    } else {
      out[name] = false
    }
  })
  return out
}

export function poVisibleForGrid(grid: SheetGrid, stored: PoVisibleByHeader, _previousGrid?: SheetGrid, defaults: boolean[] = []): boolean[] {
  return (grid[1] ?? []).map((header, col) => {
    const name = typeof header === 'string' ? header.trim() : ''
    if (!name) return false
    const uniqueInCurrent = (grid[1] ?? []).filter((current) => typeof current === 'string' && current.trim() === name).length === 1
    if (typeof stored[name] === 'boolean' && uniqueInCurrent) return stored[name]
    return defaults[col] === true
  })
}

/**
 * Đoán vai trò từ tiêu đề cột (chủ dự án chốt danh sách này). Người dùng chỉ cần
 * xác nhận hoặc sửa. Trả về null nếu tiêu đề trống.
 */
export function guessRoleFromLabel(label: SheetGridCell | undefined): SheetRole | null {
  const text = typeof label === 'string' ? label.trim() : ''
  if (!text) return null
  const key = text.toLowerCase()
  if (key === 'tên' || key === 'tên hàng') return 'identity'
  if (key === 'quy cách') return 'lookup'
  if (['màu', 'màu sắc', 'colour', 'color'].includes(key)) return 'lookup'
  if (key === 'đvt' || key === 'đơn vị') return 'uom'
  if (key === 'đơn giá') return 'compute'
  if (key === 'giá' || key === 'giá mua') return 'result'
  if (/^kt\d+$/.test(key) || ['dài', 'rộng', 'cao', 'dày', 'định lượng'].includes(key)) return 'lookup'
  return 'info'
}

/** Cột có tiêu đề nhưng chưa khai báo vai trò, theo chỉ số cột (0-based). */
export function columnsMissingRole(headers: SheetGridCell[], roles: Array<SheetRole | null>): number[] {
  const missing: number[] = []
  headers.forEach((h, c) => {
    const hasTitle = typeof h === 'string' && h.trim() !== ''
    if (hasTitle && !roles[c]) missing.push(c)
  })
  return missing
}

/**
 * Đổi ma trận `data` của FortuneSheet (ô là {v, m, f} hoặc null) sang lưới máy chủ.
 * Ô có công thức lấy chuỗi công thức; ô khác lấy giá trị gốc.
 */
export function gridFromFortuneMatrix(matrix: unknown[][]): SheetGrid {
  return matrix.map((row) =>
    (row ?? []).map((cell) => {
      if (cell == null || typeof cell !== 'object') return null
      const c = cell as { v?: unknown; f?: unknown }
      if (typeof c.f === 'string' && c.f.trim() !== '') return c.f
      if (c.v === undefined || c.v === '') return null
      if (c.v && typeof c.v === 'object') return quoteSheetCellText(c.v) || null
      return c.v as SheetGridCell
    }),
  )
}

/** Nhãn vai trò hiển thị trong hàng "Vai trò" → khóa vai trò. */
export const ROLE_BY_LABEL: Record<string, SheetRole> = Object.fromEntries(
  (Object.entries(SHEET_ROLE_LABEL) as Array<[SheetRole, string]>).map(([role, label]) => [label, role]),
)

/**
 * Đọc hàng vai trò (hàng 1 của lưới). Trả về khóa vai trò theo cột, và danh sách
 * lỗi nếu có ô ghi nhãn lạ. Ô trống là cột chưa khai báo.
 */
export function readRoleRow(roleRow: SheetGridCell[]): { roles: Array<SheetRole | null>; invalid: number[] } {
  const invalid: number[] = []
  const roles = roleRow.map((cell, c) => {
    if (cell === null || cell === '') return null
    const role = typeof cell === 'string' ? ROLE_BY_LABEL[cell.trim()] : undefined
    if (!role) {
      invalid.push(c)
      return null
    }
    return role
  })
  return { roles, invalid }
}

/** Bỏ dòng và cột trống ở cuối lưới để không gửi phần thừa lên máy chủ. */
export function trimGrid(grid: SheetGrid): SheetGrid {
  const rows = grid.map((row) => [...row])
  while (rows.length > 0 && rows[rows.length - 1].every((cell) => cell === null || cell === '')) rows.pop()
  const width = rows.reduce((max, row) => {
    let last = row.length - 1
    while (last >= 0 && (row[last] === null || row[last] === '')) last--
    return Math.max(max, last + 1)
  }, 0)
  return rows.map((row) => row.slice(0, width).map((cell) => (cell === undefined ? null : cell)))
}

/**
 * Đổi lưới máy chủ sang `celldata` để nạp vào FortuneSheet. Ô công thức chỉ đưa
 * chuỗi công thức; FortuneSheet tự tính sau khi khởi tạo.
 */
/** Số hàng đầu là hàng vai trò và hàng tiêu đề: tô màu, chữ đậm và to hơn. */
export const SHEET_HEADER_ROWS = 2

/** Màu hệ thống cho ô tiêu đề cột bật trên PO (slate trung tính). Không phải màu người dùng tô tự do. */
export const PO_ON_BG = '#e2e8f0'
export const PO_ON_FC = '#0f172a'
export const PO_OFF_BG = '#f1f5f9'
export const PO_OFF_FC = '#334155'

/** Style ô của bảng: hàng tiêu đề dùng màu trung tính (slate), chữ đậm cỡ 13; dữ liệu cỡ 12. */
function styleFor(r: number): Record<string, unknown> {
  if (r === 0) return { bg: '#f8fafc', fc: '#475569', bl: 1, fs: 13, ff: 'Inter' }
  if (r === 1) return { bg: PO_OFF_BG, fc: PO_OFF_FC, bl: 1, fs: 13, ff: 'Inter' }
  return { fs: 12, ff: 'Inter' }
}

export function celldataFromGrid(grid: SheetGrid, poVisible: boolean[] = []): Array<{ r: number; c: number; v: Record<string, unknown> }> {
  const out: Array<{ r: number; c: number; v: Record<string, unknown> }> = []
  const sttColumn = (grid[1] ?? []).findIndex((header) => typeof header === 'string' && header.trim().toLocaleLowerCase('vi') === 'stt')
  const rows = Math.max(grid.length + 30, 40)
  const columns = Math.max(0, ...grid.map((row) => row.length)) + 6
  const columnCount = Math.max(columns, 14)
  for (let r = 0; r < rows; r += 1) {
    const row = grid[r] ?? []
    for (let c = 0; c < columnCount; c += 1) {
      const cell = row[c] ?? null
      const isFormula = typeof cell === 'string' && cell.startsWith('=')
      const onPo = r === SHEET_HEADER_ROWS - 1 && poVisible[c] === true
      const base = onPo
        ? { bg: PO_ON_BG, fc: PO_ON_FC, bl: 1, fs: 13, ff: 'Inter' }
        : c === sttColumn && r >= SHEET_HEADER_ROWS ? { ...styleFor(r), bg: '#f1f5f9', fc: '#64748b' }
          : styleFor(r)
      const value = isFormula
        ? { ...base, f: cell }
        : cell === null || cell === '' ? base : { ...base, v: cell, m: String(cell) }
      out.push({ r, c, v: value })
    }
  }
  return out
}

/**
 * Viền "all borders" cho toàn vùng có dữ liệu (theo số hàng và số cột của lưới).
 * FortuneSheet đọc viền theo vùng trong config.borderInfo.
 */
export function borderInfoFor(grid: SheetGrid): Array<Record<string, unknown>> {
  const rows = grid.length
  const cols = Math.max(0, ...grid.map((row) => row.length))
  if (rows === 0 || cols === 0) return []
  return [
    {
      rangeType: 'range',
      borderType: 'border-all',
      color: '#334155',
      style: 1,
      range: [{ row: [0, rows - 1], column: [0, cols - 1] }],
    },
  ]
}

/** Chữ cột kiểu Excel từ chỉ số 0-based: 0 → A, 26 → AA. */
export function columnLetter(index: number): string {
  let n = index + 1
  let out = ''
  while (n > 0) {
    out = String.fromCharCode(65 + ((n - 1) % 26)) + out
    n = Math.floor((n - 1) / 26)
  }
  return out
}

/** Địa chỉ ô kiểu Excel: (1, 6) → "G2". */
export function addressOf(row: number, col: number): string {
  return `${columnLetter(col)}${row + 1}`
}

/** Đọc địa chỉ "G2" thành (dòng, cột) 0-based; null nếu sai định dạng. */
export function parseAddress(address: string): { row: number; col: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(address)
  if (!m) return null
  let col = 0
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64)
  return { row: Number(m[2]) - 1, col: col - 1 }
}

/** Chuẩn hóa tên để so khớp: bỏ dấu, gộp khoảng trắng, không phân biệt hoa thường. */
export function normalizeItemName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Gợi ý vật tư cho từng dòng theo tên hàng: chỉ gợi ý khi đúng một vật tư khớp tên
 * (giống luồng "Gợi ý liên kết tự động" cũ). Trả về {"<số dòng>": "<id vật tư>"}.
 * Dòng không khớp hoặc khớp nhiều vật tư thì không gợi ý, người dùng tự chọn.
 */
export function suggestItemIdsByName(
  grid: SheetGrid,
  roles: Array<SheetRole | null>,
  materials: Array<{ id: string; name: string }>,
): Record<string, string> {
  const nameCol = roles.indexOf('identity')
  if (nameCol < 0) return {}
  const byName = new Map<string, string[]>()
  for (const m of materials) {
    const key = normalizeItemName(m.name)
    if (!key) continue
    byName.set(key, [...(byName.get(key) ?? []), m.id])
  }
  const out: Record<string, string> = {}
  grid.forEach((row, r) => {
    if (r < 2) return
    const name = typeof row[nameCol] === 'string' ? normalizeItemName(row[nameCol] as string) : ''
    if (!name) return
    const ids = byName.get(name) ?? []
    if (ids.length === 1) out[String(r + 1)] = ids[0]
  })
  return out
}

/**
 * Độ rộng cột và chiều cao hàng tự căng theo nội dung (px).
 * Cột: theo ký tự dài nhất (ô công thức tính theo 10 ký tự), kẹp trong [90, 320].
 * Hàng: hàng vai trò và tiêu đề cao hơn để chữ lớn không bị cắt.
 */
export function layoutFor(grid: SheetGrid): { columnlen: Record<number, number>; rowlen: Record<number, number> } {
  const width = Math.max(0, ...grid.map((row) => row.length))
  const columnlen: Record<number, number> = {}
  for (let c = 0; c < width; c++) {
    let maxChars = 6
    for (const row of grid) {
      const cell = row[c]
      if (cell === null || cell === '') continue
      const text = typeof cell === 'string' && cell.startsWith('=') ? '0000000000' : String(cell)
      maxChars = Math.max(maxChars, text.length)
    }
    columnlen[c] = Math.min(320, Math.max(90, Math.round(maxChars * 8.5 + 28)))
  }
  const rowlen: Record<number, number> = {}
  grid.forEach((_, r) => {
    rowlen[r] = r < SHEET_HEADER_ROWS ? 36 : 26
  })
  return { columnlen, rowlen }
}

/**
 * Mô tả cách máy hiểu một dòng báo giá (quy tắc tự suy): ô có giá trị là ánh xạ,
 * ô trống được công thức dùng là nhập lúc mua. Ví dụ "Ánh xạ: KT2=90, Định lượng=10kg · Nhập lúc mua: KT1".
 */
export function describeRowClassification(mappings: Record<string, unknown> = {}, inputNames: string[] = []): string {
  const mapped = Object.entries(mappings).map(([k, v]) => `${k}=${v}`)
  const parts: string[] = []
  if (mapped.length > 0) parts.push(`Ánh xạ: ${mapped.join(', ')}`)
  parts.push(inputNames.length > 0 ? `Nhập lúc mua: ${inputNames.join(', ')}` : 'Không có ô nhập lúc mua')
  return parts.join(' · ')
}

/**
 * Dịch số dòng của các tham chiếu ô tương đối trong công thức (giống kéo điền của Excel).
 * Tham chiếu có `$` trước số dòng (ví dụ B$2) giữ nguyên. Chuỗi trong ngoặc kép và tên hàm
 * (ví dụ LOG10) không bị đụng tới.
 */
export function shiftFormulaRows(formula: string, delta: number): string {
  if (delta === 0) return formula
  const parts = formula.split('"')
  return parts
    .map((part, i) =>
      i % 2 === 1
        ? part
        : part.replace(/(?<![A-Za-z0-9_$])(\$?)([A-Za-z]{1,3})(\$?)(\d+)(?![A-Za-z0-9_(])/g, (all, colAbs, col, rowAbs, row) => {
            if (rowAbs === '$') return all
            const next = Number(row) + delta
            return next < 1 ? all : `${colAbs}${col}${rowAbs}${next}`
          }),
    )
    .join('"')
}

/**
 * Kế hoạch áp công thức của ô (sourceRow, col) xuống các dòng khác (0-based theo lưới).
 * Không truyền targetRows thì áp cho mọi dòng dữ liệu có nội dung ở cột khác, trừ dòng nguồn.
 */
export function planFillFormula(
  grid: SheetGrid,
  sourceRow: number,
  col: number,
  targetRows?: number[],
): Array<{ r: number; c: number; formula: string }> {
  const source = grid[sourceRow]?.[col]
  if (typeof source !== 'string' || !source.trim().startsWith('=')) return []
  const rows =
    targetRows ??
    grid.flatMap((row, r) =>
      r >= SHEET_HEADER_ROWS && r !== sourceRow && row.some((cell, c) => c !== col && cell !== null && cell !== '') ? [r] : [],
    )
  return rows.filter((r) => r !== sourceRow && r >= SHEET_HEADER_ROWS).map((r) => ({ r, c: col, formula: shiftFormulaRows(source, r - sourceRow) }))
}

/**
 * Chuẩn hóa grid đầu vào thành mảng 2 chiều hợp lệ.
 * Đảm bảo dữ liệu dạng đối tượng có khóa số (JSONB object hoặc mock) hay mảng hỗn hợp
 * không gây lỗi .map is not a function.
 */
export function normalizeGridInput(raw: unknown): SheetGrid {
  if (!raw || typeof raw !== 'object') return []
  let rows: unknown[] = []
  if (Array.isArray(raw)) {
    rows = raw
  } else {
    const keys = Object.keys(raw)
      .filter((k) => /^\d+$/.test(k))
      .map(Number)
      .sort((a, b) => a - b)
    rows = keys.map((k) => (raw as Record<number, unknown>)[k])
  }
  return rows.map((row) => {
    if (Array.isArray(row)) return row
    if (row && typeof row === 'object') {
      const keys = Object.keys(row)
        .filter((k) => /^\d+$/.test(k))
        .map(Number)
        .sort((a, b) => a - b)
      return keys.map((k) => (row as Record<number, unknown>)[k] as SheetGridCell)
    }
    return []
  })
}

/**
 * Chuẩn hóa cờ po_visible thành mảng boolean tương ứng với số cột của lưới.
 */
export function normalizePoVisibleInput(pv: unknown, grid: SheetGrid): boolean[] {
  if (Array.isArray(pv)) return pv.map(Boolean)
  const width = Math.max(0, ...grid.map((r) => r.length))
  if (pv && typeof pv === 'object') {
    const numericKeys = Object.keys(pv).filter((k) => /^\d+$/.test(k))
    if (numericKeys.length > 0) {
      const res: boolean[] = []
      for (let i = 0; i < width; i++) {
        res.push(Boolean((pv as Record<number, unknown>)[i]))
      }
      return res
    }
    const headers = grid[1] ?? []
    return headers.map((h) => {
      const name = typeof h === 'string' ? h.trim() : ''
      return Boolean((pv as Record<string, unknown>)[name])
    })
  }
  return []
}

